import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link } from '@coursera/cds-core';

import _t from 'i18n!nls/verification';

export type IdStateMessage = {
  contentDivText: React.ReactNode;
  ctaDivText?: string;
  ctaClassName?: string;
};

const RETRY_LIMIT = 5;
const NOT_STARTED = 'NOT_STARTED';
const APPROVED = 'APPROVED';
const DENIED_BY_PERSONA = 'DENIED_BY_PERSONA';
const DENIED_BY_JUMIO = 'DENIED_BY_JUMIO';
const ERROR_NOT_READABLE_ID = 'ERROR_NOT_READABLE_ID';
const NO_ID_UPLOADED = 'NO_ID_UPLOADED';
const DENIED_EXPIRED_ID = 'DENIED_EXPIRED_ID';
const DENIED_AGE = 'DENIED_AGE';
const DENIED_RESTRICTED_COUNTRY = 'DENIED_RESTRICTED_COUNTRY';
const ERROR_NOT_JUMIO_SUPPORTED_COUNTRY = 'ERROR_NOT_JUMIO_SUPPORTED_COUNTRY';
const ALL_RETRIES_USED = 'ALL_RETRIES_USED';
const PENDING = 'PENDING';
const FAILED = 'FAILED';
const NEEDS_TO_VERIFY_ID = 'NEEDS_TO_VERIFY_ID';

// For more detaiils on templates see here https://coursera.atlassian.net/wiki/spaces/DEG/pages/188810488/ID+Verification#Persona-Configuration-Details
const PERSONA_ENVIRONMENT = 'production';
const PERSONA_TEMPLATE_ID_NEW = 'itmpl_hXDYjWJSPKgN2zxfw6ZLMhwo';

const PERSONA_TEMPLATE_ID_FINGER_PRINTING = 'itmpl_qCWuMPC2CKEJMNkCWjcsrwSK';

const getVerificationTitles = () => ({
  ID_VERIFICATION_TITLE: _t('Verify My ID'),
  NAME_VERIFICATION_TITLE: _t('Name Verification'),
});

const getNameVerificationMessages = (fullName: $TSFixMe) => ({
  VERIFY_NAME: {
    contentDivText: _t(
      'Verify your real name to make sure you’re able to receive a certificate when you complete a course or Specialization.'
    ),
    ctaDivText: _t('Verify My Name'),
    ctaClassName: 'secondary cozy link-button',
  },

  UPDATE_NAME: {
    contentDivText: (
      <FormattedMessage
        message={_t('Your name, {fullName}, is verified. This is the name that will appear on your certificates.')}
        fullName={fullName}
      />
    ),
    ctaDivText: _t('Request Name Change'),
    ctaClassName: 'secondary grey cozy link-button',
  },
});

const getIdStateMessages = (fullName: string): Record<string, IdStateMessage> => {
  const VERIFY_ID_MESSAGE = _t('Verify My ID');
  const UPDATE_ID_MESSAGE = _t('Update My ID');

  return {
    [NEEDS_TO_VERIFY_ID]: {
      contentDivText: _t(
        'ID verification is required for select courses, including Degree and MasterTrack Certificate programs. Verify your name with your government-issued ID to make sure you earn your certificate when you complete your courses.'
      ),
      ctaDivText: VERIFY_ID_MESSAGE,
      ctaClassName: 'secondary cozy link-button',
    },
    [NOT_STARTED]: {
      contentDivText: _t(
        'ID verification is required for select courses, including Degree and MasterTrack Certificate programs. Verify your name with your government-issued ID to make sure you earn your certificate when you complete your courses.'
      ),
      ctaDivText: VERIFY_ID_MESSAGE,
      ctaClassName: 'secondary cozy link-button',
    },
    [APPROVED]: {
      contentDivText: (
        <FormattedMessage
          message={_t(
            'Your name, {fullName}, is verified. This is the name that will appear on your certificates. If you have questions or need help with your ID Verification, visit our {idVerificationSupport}.'
          )}
          fullName={fullName}
          idVerificationSupport={
            <Link href="https://learner.coursera.help/hc/articles/209818953" target="_blank" rel="noopener noreferrer">
              {_t('ID Verification support page')}
            </Link>
          }
        />
      ),
    },
    [DENIED_BY_PERSONA]: {
      contentDivText: (
        <FormattedMessage
          message={_t('Your ID could not be verified. Please try again or contact {link} for assistance.')}
          link={
            <Link href="https://learner.coursera.help/hc/articles/209818953" target="_blank" rel="noopener noreferrer">
              {_t('Coursera Learner Support')}
            </Link>
          }
        />
      ),
      ctaDivText: VERIFY_ID_MESSAGE,
      ctaClassName: 'secondary cozy link-button',
    },
    [DENIED_BY_JUMIO]: {
      contentDivText: (
        <FormattedMessage
          message={_t(
            'We couldn’t verify the ID you submitted. Please see our {photoRestrictionsLink} and our {internationalRestrictions} for more information.'
          )}
          photoRestrictionsLink={
            <Link
              href="https://learner.coursera.help/hc/articles/208280126-Requirements-for-a-valid-photo-ID"
              target="_blank"
              rel="noopener noreferrer"
            >
              {_t('requirements for photo ID')}
            </Link>
          }
          internationalRestrictions={
            <Link
              href="https://learner.coursera.help/hc/articles/208280116-International-restrictions"
              target="_blank"
              rel="noopener noreferrer"
            >
              {_t('international restrictions')}
            </Link>
          }
        />
      ),
      ctaDivText: VERIFY_ID_MESSAGE,
      ctaClassName: 'secondary cozy link-button',
    },
    [ERROR_NOT_READABLE_ID]: {
      contentDivText: _t(
        'We couldn’t verify the ID you submitted, because we couldn’t read the text in the image you submitted. Please try again and make sure the image of your ID is legible.'
      ),
      ctaDivText: VERIFY_ID_MESSAGE,
      ctaClassName: 'secondary cozy link-button',
    },
    [NO_ID_UPLOADED]: {
      contentDivText: _t(
        'We couldn’t verify the ID you submitted, because you uploaded an image that did not contain an ID. Please try again and make sure the image of your ID is legible.'
      ),
      ctaDivText: VERIFY_ID_MESSAGE,
      ctaClassName: 'secondary cozy link-button',
    },
    [DENIED_EXPIRED_ID]: {
      contentDivText: _t(
        'We couldn’t verify the ID you submitted, because your ID is expired. Please submit a valid photo ID and try again.'
      ),
      ctaDivText: VERIFY_ID_MESSAGE,
      ctaClassName: 'secondary cozy link-button',
    },
    [DENIED_AGE]: {
      contentDivText: (
        <FormattedMessage
          message={_t(
            'We couldn’t verify the ID you submitted, because you must be 13 years or older to use Coursera. More information on our age restrictions is available {link}.'
          )}
          link={
            <Link
              href="https://learner.coursera.help/hc/articles/209818923-Age-restrictions"
              target="_blank"
              rel="noopener noreferrer"
            >
              {_t('here')}
            </Link>
          }
        />
      ),
      ctaDivText: VERIFY_ID_MESSAGE,
      ctaClassName: 'secondary cozy link-button',
    },
    [DENIED_RESTRICTED_COUNTRY]: {
      contentDivText: (
        <FormattedMessage
          message={_t(
            'We couldn’t verify the ID you submitted, because we cannot offer services to users in certain sanctioned countries. More information on our international restrictions is available {link}.'
          )}
          link={
            <Link
              href="https://learner.coursera.help/hc/articles/208280116-International-restrictions"
              target="_blank"
              rel="noopener noreferrer"
            >
              {_t('here')}
            </Link>
          }
        />
      ),
      ctaDivText: VERIFY_ID_MESSAGE,
      ctaClassName: 'secondary cozy link-button',
    },
    [ERROR_NOT_JUMIO_SUPPORTED_COUNTRY]: {
      contentDivText: (
        <FormattedMessage
          message={_t('We’re sorry but we need to manually review your ID. Please contact {link} for assistance.')}
          link={
            <Link href="https://learner.coursera.help/hc/articles/209818953" target="_blank" rel="noopener noreferrer">
              {_t('Coursera Learner Support')}
            </Link>
          }
        />
      ),
      ctaDivText: VERIFY_ID_MESSAGE,
      ctaClassName: 'secondary cozy link-button',
    },
    [ALL_RETRIES_USED]: {
      contentDivText: (
        <FormattedMessage
          message={_t(
            'We couldn’t verify the ID you submitted. For security purposes, we limit ID verification to {RETRY_LIMIT} attempts. Visit the Learner Help Center for more information.'
          )}
          RETRY_LIMIT={RETRY_LIMIT}
        />
      ),
    },
    [PENDING]: {
      contentDivText: _t(
        'Thank you for beginning your ID verification process. Once your ID has been approved, we’ll update this page. ID approval takes about 24 hours.'
      ),
      ctaDivText: UPDATE_ID_MESSAGE,
      ctaClassName: 'secondary grey cozy link-button',
    },
    [FAILED]: {
      contentDivText: _t('Something went wrong, please contact support and we will assist with verifying your ID'),
    },
  };
};

export default {
  getVerificationTitles,
  getNameVerificationMessages,
  getIdStateMessages,
  RETRY_LIMIT,
  NOT_STARTED,
  APPROVED,
  DENIED_BY_JUMIO,
  DENIED_BY_PERSONA,
  ERROR_NOT_READABLE_ID,
  NO_ID_UPLOADED,
  DENIED_EXPIRED_ID,
  DENIED_AGE,
  DENIED_RESTRICTED_COUNTRY,
  ERROR_NOT_JUMIO_SUPPORTED_COUNTRY,
  ALL_RETRIES_USED,
  PENDING,
  FAILED,
  NEEDS_TO_VERIFY_ID,
};

export {
  getVerificationTitles,
  getNameVerificationMessages,
  getIdStateMessages,
  RETRY_LIMIT,
  NOT_STARTED,
  APPROVED,
  DENIED_BY_JUMIO,
  DENIED_BY_PERSONA,
  ERROR_NOT_READABLE_ID,
  NO_ID_UPLOADED,
  DENIED_EXPIRED_ID,
  DENIED_AGE,
  DENIED_RESTRICTED_COUNTRY,
  ERROR_NOT_JUMIO_SUPPORTED_COUNTRY,
  ALL_RETRIES_USED,
  PENDING,
  FAILED,
  PERSONA_TEMPLATE_ID_NEW,
  PERSONA_TEMPLATE_ID_FINGER_PRINTING,
  PERSONA_ENVIRONMENT,
};
